import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Rnd } from "react-rnd";
import { toast } from "sonner";
import {
  ShirtIcon,
  Palette,
  Store,
  Ruler,
  Plus,
  Minus,
  MapPin,
  UserRound,
  Send,
  Settings2,
  ImageOff,
  Download,
  FileText,
  Truck,
} from "lucide-react";
import html2canvas from "html2canvas";

import { api, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import logoLetrafilme from "@/assets/logo-letrafilme.webp";

const TSHIRT_CATEGORIES = [
  { key: "masculino", label: "Masculino" },
  { key: "feminino", label: "Feminino" },
  { key: "cropped", label: "Cropped" },
  { key: "infantil", label: "Infantil" },
];

const PRINT_ARTISTS = [
  { key: "odilea", label: "Odiléa Toscano" },
  { key: "orlando", label: "Orlando Setti" },
];

// Normaliza telefone BR para o formato internacional usado em wa.me
// Aceita entradas como: "11 99999-9999", "(11) 99999-9999", "+55 11 99999-9999",
// "011 9999-9999", "5511999999999". Retorna só dígitos no formato 55DDDNUMERO.
const normalizeBrPhone = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  // Remove IDD "00" se presente
  if (d.startsWith("00")) d = d.slice(2);
  // Já internacional (ex.: 5511...)
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) return d;
  // Remove "0" inicial do DDD (ex.: "011999...")
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  if (d.length === 12 && d.startsWith("0")) d = d.slice(1);
  // DDD + número (10 ou 11 dígitos) → acrescenta DDI 55
  if (d.length === 10 || d.length === 11) return "55" + d;
  return d;
};

const SIZES = ["6", "8", "10", "12", "14", "P", "M", "G", "GG"];

export default function Storefront() {
  const [config, setConfig] = useState({ whatsapp: "", price: 0, shop_name: "Estampa Livre" });
  const [tshirts, setTshirts] = useState([]);
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTshirt, setSelectedTshirt] = useState(null);
  const [selectedPrint, setSelectedPrint] = useState(null);
  const [activeCategory, setActiveCategory] = useState("masculino");
  const [activeArtist, setActiveArtist] = useState("odilea");

  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [cpf, setCpf] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [cep, setCep] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [freight, setFreight] = useState(null); // selected service_id
  const [freightOptions, setFreightOptions] = useState([]);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightError, setFreightError] = useState("");
  const [addr, setAddr] = useState({
    rua: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
  });
  const composedAddress = () => {
    const parts = [
      [addr.rua, addr.numero].filter(Boolean).join(", "),
      addr.complemento,
      addr.bairro,
      [addr.cidade, addr.uf].filter(Boolean).join("/"),
      cep ? `CEP ${cep}` : "",
    ].filter(Boolean);
    return parts.join(" — ");
  };
  const address = composedAddress();

  const lookupCep = async (raw) => {
    const clean = String(raw || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      setAddr((a) => ({
        ...a,
        rua: data.logradouro || a.rua,
        bairro: data.bairro || a.bairro,
        cidade: data.localidade || a.cidade,
        uf: data.uf || a.uf,
        complemento: data.complemento || a.complemento,
      }));
      toast.success("Endereço preenchido pelo CEP");
    } catch {
      toast.error("Falha ao buscar o CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const [stageEl, setStageEl] = useState(null);
  const stageRef = setStageEl;
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [printBox, setPrintBox] = useState(null); // {leftPct, topPct, widthPct, heightPct}
  const summaryRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const generateSummaryBlob = async () => {
    if (!summaryRef.current) return null;
    const canvas = await html2canvas(summaryRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });
    return await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
    );
  };

  const handleDownloadSummary = async () => {
    if (!summaryRef.current) return;
    setDownloading(true);
    try {
      const blob = await generateSummaryBlob();
      if (!blob) throw new Error("blob null");
      const link = document.createElement("a");
      const safeName = (customerName || "cliente")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-");
      link.download = `pedido-${safeName}-${Date.now()}.jpg`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Resumo baixado como JPG");
    } catch (e) {
      toast.error("Não foi possível gerar a imagem");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, t, p] = await Promise.all([
          api.get("/config"),
          api.get("/tshirts"),
          api.get("/prints"),
        ]);
        setConfig(c.data);
        setTshirts(t.data);
        setPrints(p.data);
        const firstByCat = t.data.find(
          (x) => (x.category || "masculino") === "masculino"
        );
        if (firstByCat) setSelectedTshirt(firstByCat);
        else if (t.data[0]) setSelectedTshirt(t.data[0]);
        const firstPrint = p.data.find(
          (x) => (x.category || "odilea") === "odilea"
        );
        if (firstPrint) setSelectedPrint(firstPrint);
        else if (p.data[0]) setSelectedPrint(p.data[0]);
      } catch (e) {
        toast.error("Erro ao carregar a loja");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!stageEl) return;
    const update = () => {
      const rect = stageEl.getBoundingClientRect();
      if (rect.width > 0) setStageSize({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);

  // Compute print size proportional to shirt, centered inside the shirt's print area
  useEffect(() => {
    if (!selectedPrint) {
      setPrintBox(null);
      return;
    }
    const category = selectedTshirt?.category || "";
    const isKids = category === "infantil";
    const isCropped = category === "cropped";
    // Base area: 35%. Kids: -30%. Cropped: +8% and shifted up. (fallback when no per-shirt area)
    const AREA = isKids ? 0.35 * 0.7 : isCropped ? 0.35 * 1.08 : 0.35;
    const yOffsetPct = isCropped ? -10 : 0;

    // Per-shirt area overrides the category default entirely
    const shirtArea = selectedTshirt?.print_area;
    const areaLeft = shirtArea ? shirtArea.leftPct : 50 - (AREA * 100) / 2;
    const areaTop = shirtArea
      ? shirtArea.topPct
      : 50 - (AREA * 100) / 2 + yOffsetPct;
    const areaW = shirtArea ? shirtArea.widthPct : AREA * 100;
    const areaH = shirtArea ? shirtArea.heightPct : AREA * 100;

    const applyFit = (naturalW, naturalH) => {
      const ratio = (naturalW || 1) / (naturalH || 1);
      let widthPct, heightPct;
      // Fit inside the (rectangular) print area maintaining aspect ratio
      // treat pcts as if the stage was 100x100; the areas above are in %
      if (ratio >= areaW / areaH) {
        widthPct = areaW;
        heightPct = widthPct / ratio;
      } else {
        heightPct = areaH;
        widthPct = heightPct * ratio;
      }
      setPrintBox({
        widthPct,
        heightPct,
        leftPct: areaLeft + (areaW - widthPct) / 2,
        topPct: areaTop + (areaH - heightPct) / 2,
      });
    };
    const img = new Image();
    img.onload = () => applyFit(img.naturalWidth, img.naturalHeight);
    img.onerror = () => applyFit(1, 1);
    img.src = selectedPrint.image_base64;
    if (img.complete && img.naturalWidth > 0) {
      applyFit(img.naturalWidth, img.naturalHeight);
    }
  }, [selectedPrint?.id, selectedTshirt?.id, selectedTshirt?.category]);

  const subtotal = useMemo(
    () => Number(config.price || 0) * Number(quantity || 0),
    [config.price, quantity]
  );

  const fetchFreight = async (destCep, qty) => {
    const clean = String(destCep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setFreightLoading(true);
    setFreightError("");
    try {
      const res = await api.post("/shipping/quote", {
        destination_cep: clean,
        quantity: Math.max(1, Number(qty || 1)),
      });
      setFreightOptions(res.data.quotes || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Não foi possível calcular o frete";
      setFreightError(detail);
      setFreightOptions([]);
    } finally {
      setFreightLoading(false);
    }
  };

  // Refetch freight when CEP or quantity changes
  useEffect(() => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length === 8 && quantity) {
      fetchFreight(clean, quantity);
    } else {
      setFreightOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cep, quantity]);

  const selectedFreight = useMemo(
    () => freightOptions.find((o) => o.service_id === freight) || null,
    [freightOptions, freight]
  );

  // Reset selected freight if it's no longer available
  useEffect(() => {
    if (freight && !freightOptions.some((o) => o.service_id === freight)) {
      setFreight(null);
    }
  }, [freight, freightOptions]);

  // Exit edit mode when clicking anywhere outside the print/Rnd
  useEffect(() => {
    if (!editMode) return;
    const handler = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(".react-draggable")) return;
      setEditMode(false);
    };
    // Delay attaching the listener so the click that entered edit mode
    // isn't caught by the same handler.
    const attach = setTimeout(() => {
      document.addEventListener("mousedown", handler);
      document.addEventListener("touchstart", handler);
    }, 0);
    return () => {
      clearTimeout(attach);
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [editMode]);

  const total = subtotal + (selectedFreight?.price || 0);

  const handleSubmit = async () => {
    if (!selectedTshirt || !selectedPrint) {
      toast.error("Escolha uma camiseta e uma estampa");
      return;
    }
    if (!size) {
      toast.error("Escolha um tamanho");
      return;
    }
    if (!quantity || Number(quantity) < 1) {
      toast.error("Informe a quantidade");
      return;
    }
    if (!selectedFreight) {
      toast.error("Escolha uma opção de frete");
      return;
    }
    if (!customerName.trim()) {
      toast.error("Preencha seu nome");
      return;
    }
    if (cpf.replace(/\D/g, "").length !== 11) {
      toast.error("Informe um CPF válido");
      return;
    }
    if (customerPhone.replace(/\D/g, "").length < 10) {
      toast.error("Informe um telefone válido com DDD");
      return;
    }
    if (!addr.rua || !addr.numero || !addr.bairro || !addr.cidade || !addr.uf) {
      toast.error("Complete o endereço (CEP, rua, número, bairro e cidade)");
      return;
    }
    if (!config.whatsapp) {
      toast.error("Vendedor ainda não configurou o WhatsApp");
      return;
    }
    const phone = normalizeBrPhone(config.whatsapp);
    if (!phone || phone.length < 12 || phone.length > 13) {
      toast.error(
        "Número de WhatsApp do vendedor inválido. Peça para ele configurar no formato (ex: 11999999999)."
      );
      return;
    }
    // Open the tab synchronously (before any await) so popup blockers allow it
    const canShareFiles =
      !!navigator.canShare &&
      navigator.canShare({ files: [new File([""], "t.jpg", { type: "image/jpeg" })] });
    const waWindow = canShareFiles ? null : window.open("", "_blank");
    const openWhatsApp = (url) => {
      if (waWindow && !waWindow.closed) waWindow.location.href = url;
      else window.location.href = url;
    };
    try {
      await api.post("/orders", {
        customer_name: customerName,
        cpf,
        phone: customerPhone,
        address,
        size,
        quantity,
        tshirt_id: selectedTshirt.id,
        tshirt_name: selectedTshirt.name,
        print_id: selectedPrint.id,
        print_name: selectedPrint.name,
        unit_price: Number(config.price || 0),
        total,
      });

      const lines = [
        `*Novo Pedido - ${config.shop_name || "Estampa Livre"}*`,
        ``,
        `👤 *Cliente:* ${customerName}`,
        `🆔 *CPF:* ${cpf}`,
        `📞 *Telefone:* ${customerPhone}`,
        `📍 *Endereço:* ${address}`,
        ``,
        `👕 *Camiseta:* ${selectedTshirt.name}${
          selectedTshirt.category
            ? ` (${
                TSHIRT_CATEGORIES.find((c) => c.key === selectedTshirt.category)
                  ?.label || selectedTshirt.category
              })`
            : ""
        }`,
        `🎨 *Estampa:* ${selectedPrint.name}${
          selectedPrint.category
            ? ` — Artista ${
                PRINT_ARTISTS.find((a) => a.key === selectedPrint.category)
                  ?.label || selectedPrint.category
              }`
            : ""
        }`,
        `📏 *Tamanho:* ${size}`,
        `🔢 *Quantidade:* ${quantity}`,
        ``,
        `💵 *Valor unitário:* ${brl(config.price)}`,
        `📦 *Frete:* ${selectedFreight.carrier} — ${selectedFreight.service_name} — ${brl(
          selectedFreight.price
        )} (${
          selectedFreight.delivery_min && selectedFreight.delivery_max
            ? `${selectedFreight.delivery_min} a ${selectedFreight.delivery_max} dias úteis`
            : `${selectedFreight.delivery_time || "?"} dias úteis`
        })`,
        `💰 *Total:* ${brl(total)}`,
      ].join("\n");

      const waUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(lines)}`;

      // Generate the summary JPG
      let file = null;
      try {
        const blob = await generateSummaryBlob();
        if (blob) {
          file = new File([blob], `pedido-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
        }
      } catch (err) {
        console.error("Falha ao gerar imagem do pedido:", err);
      }

      // Try Web Share API with the image (works on mobile → picks WhatsApp)
      if (
        file &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            text: lines,
            title: `Pedido - ${config.shop_name || "Estampa Livre"}`,
          });
          toast.success("Pedido compartilhado com o vendedor!");
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return; // user cancelled
          // fall through to download + wa.me
        }
      }

      // Fallback (desktop/unsupported): auto-download JPG + open WhatsApp
      if (file) {
        const link = document.createElement("a");
        const safeName = (customerName || "cliente")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-");
        link.download = `pedido-${safeName}.jpg`;
        link.href = URL.createObjectURL(file);
        link.click();
        URL.revokeObjectURL(link.href);
        toast.info(
          "Imagem do pedido baixada — anexe no WhatsApp que abriu ao lado.",
          { duration: 6000 }
        );
      }
      openWhatsApp(waUrl);
    } catch (e) {
      if (waWindow && !waWindow.closed) waWindow.close();
      toast.error("Não foi possível enviar o pedido");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-6">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="w-72 h-72 sm:w-96 sm:h-96 object-contain"
          data-testid="loading-video"
        >
          <source src="/loading.webm" type="video/webm" />
          <source src="/loading.mp4" type="video/mp4" />
        </video>
        <p className="text-sm uppercase tracking-[0.3em] text-gray-500">
          Carregando a loja
        </p>
      </div>
    );
  }

  const showEmptyState = tshirts.length === 0 || prints.length === 0;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="h-10 w-10 rounded-xl bg-[#FF5722] flex items-center justify-center">
              <ShirtIcon className="text-white" size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="shop-name">
                {config.shop_name || "Estampa Livre"}
              </h1>
              <p className="text-xs text-gray-500">
                Monte sua camiseta na Letrafilmedesign
              </p>
            </div>
            <a
              href="https://www.letrafilmedesign.com.br"
              target="_blank"
              rel="noreferrer"
              className="ml-2 inline-flex items-center"
              aria-label="Letrafilmedesign"
              data-testid="letrafilme-logo"
            >
              <img
                src={logoLetrafilme}
                alt="Letrafilmedesign"
                className="h-14 w-14 object-contain"
                draggable={false}
              />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {showEmptyState ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Preview - sticky left */}
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Prévia</h2>
                      <p className="text-sm text-gray-500">
                        Este modelo simulado não representa o tamanho padrão impresso na sua camiseta.
                      </p>
                    </div>
                  </div>

                  <div
                    ref={stageRef}
                    className="tshirt-preview-stage relative w-full aspect-square rounded-xl overflow-hidden border border-gray-200"
                    data-testid="preview-stage"
                  >
                    {selectedTshirt && (
                      <img
                        src={selectedTshirt.image_base64}
                        alt={selectedTshirt.name}
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                        draggable={false}
                      />
                    )}

                    {selectedPrint && printBox && stageSize.w > 0 && (
                      <Rnd
                        size={{
                          width: (printBox.widthPct / 100) * stageSize.w,
                          height: (printBox.heightPct / 100) * stageSize.h,
                        }}
                        position={{
                          x: (printBox.leftPct / 100) * stageSize.w,
                          y: (printBox.topPct / 100) * stageSize.h,
                        }}
                        bounds="parent"
                        lockAspectRatio
                        disableDragging={!editMode}
                        enableResizing={editMode}
                        onDragStop={(_, d) =>
                          setPrintBox((p) => ({
                            ...p,
                            leftPct: (d.x / stageSize.w) * 100,
                            topPct: (d.y / stageSize.h) * 100,
                          }))
                        }
                        onResizeStop={(_, __, ref, ___, position) =>
                          setPrintBox({
                            leftPct: (position.x / stageSize.w) * 100,
                            topPct: (position.y / stageSize.h) * 100,
                            widthPct: (parseFloat(ref.style.width) / stageSize.w) * 100,
                            heightPct: (parseFloat(ref.style.height) / stageSize.h) * 100,
                          })
                        }
                        onMouseDown={(e) => {
                          if (!editMode) {
                            e.stopPropagation();
                            setEditMode(true);
                          }
                        }}
                        onTouchStart={(e) => {
                          if (!editMode) {
                            setEditMode(true);
                          }
                        }}
                        className={
                          editMode
                            ? "border-2 border-dashed border-[#FF5722] cursor-move"
                            : "cursor-pointer"
                        }
                      >
                        <img
                          src={selectedPrint.image_base64}
                          alt={selectedPrint.name}
                          className="w-full h-full object-contain pointer-events-none select-none"
                          draggable={false}
                        />
                      </Rnd>
                    )}
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">
                        Total
                      </p>
                      <p
                        className="text-3xl font-bold text-gray-900"
                        data-testid="preview-total"
                      >
                        {brl(total)}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <p>
                        {quantity ? `${quantity} un.` : "—"} × {brl(config.price)}
                      </p>
                      <p>Tam. {size || "—"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Configurator - right column */}
            <div className="lg:col-span-2 space-y-6">
              <Section title="1. Escolha a estampa dos Artistas" icon={<Palette size={18} />}>
                <div className="flex gap-2 mb-4 border-b border-gray-200 -mx-1 px-1">
                  {PRINT_ARTISTS.map((a) => {
                    const count = prints.filter(
                      (p) => (p.category || "odilea") === a.key
                    ).length;
                    const active = activeArtist === a.key;
                    return (
                      <button
                        key={a.key}
                        onClick={() => setActiveArtist(a.key)}
                        data-testid={`artist-tab-${a.key}`}
                        className={`px-3 py-2 text-sm font-medium border-b-2 transition ${
                          active
                            ? "border-[#FF5722] text-[#FF5722]"
                            : "border-transparent text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {a.label}
                        <span className="ml-1.5 text-xs text-gray-400">
                          ({count})
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const filtered = prints.filter(
                    (p) => (p.category || "odilea") === activeArtist
                  );
                  if (filtered.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 py-6 text-center">
                        Nenhuma estampa deste artista ainda.
                      </p>
                    );
                  }
                  return (
                    <MediaGrid
                      items={filtered}
                      selectedId={selectedPrint?.id}
                      onSelect={setSelectedPrint}
                      checker
                      testidPrefix="print-option"
                    />
                  );
                })()}
              </Section>

              <Section
                title="2. Escolha a camiseta"
                icon={<ShirtIcon size={18} />}
              >
                <div className="flex gap-2 mb-4 border-b border-gray-200 -mx-1 px-1">
                  {TSHIRT_CATEGORIES.map((c) => {
                    const count = tshirts.filter(
                      (t) => (t.category || "masculino") === c.key
                    ).length;
                    const active = activeCategory === c.key;
                    return (
                      <button
                        key={c.key}
                        onClick={() => setActiveCategory(c.key)}
                        data-testid={`tshirt-tab-${c.key}`}
                        className={`px-3 py-2 text-sm font-medium border-b-2 transition ${
                          active
                            ? "border-[#FF5722] text-[#FF5722]"
                            : "border-transparent text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {c.label}
                        <span className="ml-1.5 text-xs text-gray-400">
                          ({count})
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const filtered = tshirts.filter(
                    (t) => (t.category || "masculino") === activeCategory
                  );
                  if (filtered.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 py-6 text-center">
                        Nenhuma camiseta nesta categoria ainda.
                      </p>
                    );
                  }
                  return (
                    <MediaGrid
                      items={filtered}
                      selectedId={selectedTshirt?.id}
                      onSelect={setSelectedTshirt}
                      testidPrefix="tshirt-option"
                    />
                  );
                })()}
              </Section>

              <Section title="3. Tamanho e quantidade" icon={<Ruler size={18} />}>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      data-testid={`size-${s}`}
                      className={`h-11 rounded-lg border text-sm font-semibold transition
                        ${
                          size === s
                            ? "bg-[#FF5722] text-white border-[#FF5722]"
                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                        }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setQuantity((q) => Math.max(0, Number(q || 0) - 1) || "")
                    }
                    data-testid="qty-decrease"
                  >
                    <Minus size={16} />
                  </Button>
                  <Input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQuantity(v === "" ? "" : Math.max(0, parseInt(v, 10) || 0));
                    }}
                    placeholder="Quantidade"
                    className="flex-1 h-11 text-center text-lg font-semibold"
                    data-testid="qty-value"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity((q) => Number(q || 0) + 1)}
                    data-testid="qty-increase"
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </Section>

              <Section title="4. Seus dados" icon={<UserRound size={18} />}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome completo</Label>
                    <Input
                      id="name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Ex.: Maria Silva"
                      data-testid="customer-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cpf">CPF</Label>
                    <Input
                      id="cpf"
                      inputMode="numeric"
                      value={cpf}
                      onChange={(e) => {
                        const d = e.target.value.replace(/\D/g, "").slice(0, 11);
                        let formatted = d;
                        if (d.length > 9)
                          formatted = `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
                        else if (d.length > 6)
                          formatted = `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
                        else if (d.length > 3)
                          formatted = `${d.slice(0, 3)}.${d.slice(3)}`;
                        setCpf(formatted);
                      }}
                      placeholder="000.000.000-00"
                      data-testid="cpf-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer-phone">Telefone / WhatsApp</Label>
                    <Input
                      id="customer-phone"
                      type="tel"
                      inputMode="numeric"
                      value={customerPhone}
                      onChange={(e) => {
                        const d = e.target.value.replace(/\D/g, "").slice(0, 11);
                        let f = d;
                        if (d.length > 10) f = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
                        else if (d.length > 6) f = `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
                        else if (d.length > 2) f = `(${d.slice(0, 2)}) ${d.slice(2)}`;
                        setCustomerPhone(f);
                      }}
                      placeholder="(00) 00000-0000"
                      data-testid="customer-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cep" className="flex items-center gap-1">
                      <MapPin size={12} /> CEP
                    </Label>
                    <Input
                      id="cep"
                      inputMode="numeric"
                      value={cep}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                        const formatted =
                          digits.length > 5
                            ? `${digits.slice(0, 5)}-${digits.slice(5)}`
                            : digits;
                        setCep(formatted);
                        if (digits.length === 8) lookupCep(digits);
                      }}
                      onBlur={() => lookupCep(cep)}
                      placeholder="00000-000"
                      data-testid="cep-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {cepLoading
                        ? "Buscando endereço..."
                        : "Ao preencher o CEP, o endereço é completado automaticamente."}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label htmlFor="rua">Rua / Logradouro</Label>
                      <Input
                        id="rua"
                        value={addr.rua}
                        onChange={(e) => setAddr((a) => ({ ...a, rua: e.target.value }))}
                        placeholder="Rua Exemplo"
                        data-testid="addr-rua-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="numero">Número</Label>
                      <Input
                        id="numero"
                        value={addr.numero}
                        onChange={(e) =>
                          setAddr((a) => ({ ...a, numero: e.target.value }))
                        }
                        placeholder="123"
                        data-testid="addr-numero-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="complemento">Complemento (opcional)</Label>
                    <Input
                      id="complemento"
                      value={addr.complemento}
                      onChange={(e) =>
                        setAddr((a) => ({ ...a, complemento: e.target.value }))
                      }
                      placeholder="Apto 42, Bloco B"
                      data-testid="addr-complemento-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={addr.bairro}
                      onChange={(e) => setAddr((a) => ({ ...a, bairro: e.target.value }))}
                      placeholder="Centro"
                      data-testid="addr-bairro-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label htmlFor="cidade">Cidade</Label>
                      <Input
                        id="cidade"
                        value={addr.cidade}
                        onChange={(e) =>
                          setAddr((a) => ({ ...a, cidade: e.target.value }))
                        }
                        placeholder="São Paulo"
                        data-testid="addr-cidade-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="uf">UF</Label>
                      <Input
                        id="uf"
                        maxLength={2}
                        value={addr.uf}
                        onChange={(e) =>
                          setAddr((a) => ({ ...a, uf: e.target.value.toUpperCase() }))
                        }
                        placeholder="SP"
                        data-testid="addr-uf-input"
                      />
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="5. Frete" icon={<Truck size={18} />}>
                {!cep || cep.replace(/\D/g, "").length !== 8 ? (
                  <p className="text-sm text-gray-500 py-4">
                    Informe o CEP no passo 4 para calcularmos as opções de frete.
                  </p>
                ) : !quantity ? (
                  <p className="text-sm text-gray-500 py-4">
                    Informe a quantidade no passo 3 para calcular o frete.
                  </p>
                ) : freightLoading ? (
                  <p className="text-sm text-gray-500 py-4">
                    Calculando fretes reais com Melhor Envio...
                  </p>
                ) : freightError ? (
                  <p className="text-sm text-red-600 py-4">{freightError}</p>
                ) : freightOptions.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">
                    Nenhuma transportadora disponível para este CEP.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      Cotações reais via Melhor Envio para{" "}
                      <strong>
                        {addr.cidade || "seu CEP"}
                        {addr.uf ? `/${addr.uf}` : ""}
                      </strong>
                      . Escolha a opção que preferir:
                    </p>
                    {freightOptions.map((opt) => {
                      const active = freight === opt.service_id;
                      const eta =
                        opt.delivery_min && opt.delivery_max
                          ? `${opt.delivery_min} a ${opt.delivery_max} dias úteis`
                          : opt.delivery_time
                          ? `${opt.delivery_time} dias úteis`
                          : "";
                      return (
                        <button
                          key={opt.service_id}
                          onClick={() => setFreight(opt.service_id)}
                          data-testid={`freight-${opt.service_id}`}
                          className={`w-full text-left p-3 rounded-xl border transition flex items-center justify-between gap-3 ${
                            active
                              ? "border-[#FF5722] bg-orange-50"
                              : "border-gray-200 hover:border-gray-400 bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-4 w-4 rounded-full border-2 shrink-0 ${
                                active
                                  ? "border-[#FF5722] bg-[#FF5722]"
                                  : "border-gray-300"
                              }`}
                            />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {opt.carrier} — {opt.service_name}
                              </p>
                              <p className="text-xs text-gray-500">{eta}</p>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-gray-900">
                            {brl(opt.price)}
                          </span>
                        </button>
                      );
                    })}
                    <p className="text-[11px] text-gray-400 pt-1">
                      Valores em tempo real fornecidos pelo Melhor Envio.
                    </p>
                  </div>
                )}
              </Section>

              <Section
                title="6. Resumo do pedido"
                icon={<FileText size={18} />}
              >
                <div
                  ref={summaryRef}
                  className="bg-white rounded-xl border border-gray-200 p-5"
                  data-testid="order-summary"
                >
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                    <div className="h-9 w-9 rounded-lg bg-[#FF5722] flex items-center justify-center text-white font-bold">
                      EL
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {config.shop_name || "Estampa Livre"}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Resumo do pedido
                      </p>
                    </div>
                  </div>

                  {selectedTshirt && (
                    <div className="mt-4 mb-2">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                        Prévia da camiseta
                      </p>
                      <div
                        className="tshirt-preview-stage relative w-full aspect-square rounded-lg overflow-hidden border border-gray-200"
                      >
                        <img
                          src={selectedTshirt.image_base64}
                          alt="Camiseta"
                          crossOrigin="anonymous"
                          className="absolute inset-0 w-full h-full object-contain"
                        />
                        {selectedPrint && printBox && (
                          <img
                            src={selectedPrint.image_base64}
                            alt="Estampa"
                            crossOrigin="anonymous"
                            className="absolute object-contain"
                            style={{
                              left: `${printBox.leftPct}%`,
                              top: `${printBox.topPct}%`,
                              width: `${printBox.widthPct}%`,
                              height: `${printBox.heightPct}%`,
                            }}
                          />
                        )}                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 py-4">
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 aspect-square flex items-center justify-center">
                        {selectedTshirt ? (
                          <img
                            src={selectedTshirt.image_base64}
                            alt="Camiseta"
                            className="w-full h-full object-contain p-2"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            Sem camiseta
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5 text-[11px] text-gray-500 border-t border-gray-100">
                        Camiseta
                        <p className="text-xs font-medium text-gray-800 truncate">
                          {selectedTshirt?.name || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                      <div className="checker-bg aspect-square flex items-center justify-center">
                        {selectedPrint ? (
                          <img
                            src={selectedPrint.image_base64}
                            alt="Estampa"
                            className="w-full h-full object-contain p-2"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            Sem estampa
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5 text-[11px] text-gray-500 border-t border-gray-100">
                        Estampa
                        <p className="text-xs font-medium text-gray-800 truncate">
                          {selectedPrint?.name || "—"}
                        </p>
                        {selectedPrint?.category && (
                          <p className="text-[10px] text-gray-500 truncate">
                            Artista{" "}
                            {PRINT_ARTISTS.find(
                              (a) => a.key === selectedPrint.category
                            )?.label || selectedPrint.category}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <dl className="text-sm space-y-2 py-3 border-t border-gray-100">
                    <SummaryRow label="Cliente" value={customerName || "—"} />
                    <SummaryRow label="CPF" value={cpf || "—"} />
                    <SummaryRow label="Telefone" value={customerPhone || "—"} />
                    <SummaryRow label="Endereço" value={address || "—"} />
                    <SummaryRow label="Tamanho" value={size || "—"} />
                    <SummaryRow
                      label="Quantidade"
                      value={quantity ? `${quantity} un.` : "—"}
                    />
                    <SummaryRow
                      label="Valor unitário"
                      value={brl(config.price)}
                    />
                    <SummaryRow label="Subtotal" value={brl(subtotal)} />
                    <SummaryRow
                      label="Frete"
                      value={
                        selectedFreight
                          ? `${selectedFreight.carrier} — ${selectedFreight.service_name} — ${brl(selectedFreight.price)}`
                          : "—"
                      }
                    />
                  </dl>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-sm text-gray-500">Total</span>
                    <span className="text-2xl font-bold text-[#FF5722]">
                      {brl(total)}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={handleDownloadSummary}
                  disabled={downloading}
                  data-testid="download-summary-btn"
                >
                  <Download size={16} />{" "}
                  {downloading ? "Gerando imagem..." : "Baixar resumo em JPG"}
                </Button>
              </Section>

              <Separator />

              <Button
                className="w-full h-14 text-base brand-btn"
                onClick={handleSubmit}
                data-testid="submit-order-btn"
              >
                <Send size={18} /> Enviar pedido pelo WhatsApp — {brl(total)}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                No celular a imagem do pedido é anexada direto no WhatsApp. No
                computador, o JPG é baixado automaticamente para você anexar na
                conversa.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4 text-gray-900">
        <span className="text-[#FF5722]">{icon}</span>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs uppercase tracking-wider text-gray-500 shrink-0">
        {label}
      </dt>
      <dd className="text-xs text-gray-900 text-right break-words max-w-[65%]">
        {value}
      </dd>
    </div>
  );
}

function MediaGrid({ items, selectedId, onSelect, checker = false, testidPrefix }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item) => {
        const isSelected = item.id === selectedId;
        return (
          <button
            key={item.id}
            onClick={() => onSelect(item)}
            data-testid={`${testidPrefix}-${item.id}`}
            className={`card-hover relative aspect-square rounded-xl overflow-hidden border ${
              isSelected ? "selectable-ring border-transparent" : "border-gray-200"
            } ${checker ? "checker-bg" : "bg-gray-50"}`}
          >
            <img
              src={item.image_base64}
              alt={item.name}
              className="w-full h-full object-contain p-2"
            />
            <span className="absolute bottom-1 left-1 right-1 text-[10px] font-medium text-gray-700 bg-white/85 rounded px-1.5 py-0.5 truncate">
              {item.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-24">
      <div className="mx-auto h-16 w-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4">
        <ImageOff className="text-[#FF5722]" size={28} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Loja em breve
      </h2>
      <p className="text-gray-500 mb-6">
        Estamos preparando nossos produtos. Volte em instantes!
      </p>
    </div>
  );
}
